import { Link, Stack, Typography, useTheme } from "@mui/material";
import _ from "lodash";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo } from "react";
import {
  Accordion,
  AccordionSkeleton,
  AccordionTitle,
  ActionItemCard,
  ActionItemsTable,
  SnackbarContent,
  useDrawer,
  ViewList,
} from "../../../components";
import { ACTION_ITEM_STATUSES, Links, UserStrategyFilterByOption, UserStrategyGroupByOption } from "../../../constants";
import { useSession } from "../../../hooks";
import { ActionItemSummaryDto, useGetUserStrategicOwnership, UserDto } from "../../../http";
import { useUserStrategySettings } from "./hooks";

interface IProps {
  userGuid: string;
  isTableView: boolean;
  onLoaded?: () => void;
}

const UserStrategicOwnershipContent = (props: IProps) => {
  const { userId: sessionUserGuid } = useSession();
  const { settings } = useUserStrategySettings(props.userGuid);
  const { data, isLoading } = useGetUserStrategicOwnership(props.userGuid, {
    filters: {
      [UserStrategyFilterByOption.Status]: settings.filterByStatusOptions?.map((x) => x.toString()) ?? null,
    },
    sortProperty: settings.sortByOption,
    sortDirection: settings.sortByDirection,
  });
  const { closeDrawer } = useDrawer();
  const theme = useTheme();
  const { onLoaded } = props;

  useEffect(() => {
    if (!isLoading) {
      onLoaded?.();
    }
  }, [isLoading, onLoaded]);

  const groupByFunction = useCallback(
    (item: ActionItemSummaryDto) => {
      switch (settings.groupByOption) {
        case UserStrategyGroupByOption.None:
          return "All Items";
        case UserStrategyGroupByOption.Team:
          return item.teamPlanSection.teamName;
        case UserStrategyGroupByOption.Status:
          return ACTION_ITEM_STATUSES.find((x) => x.id === item.currentStatus)?.name;
        case UserStrategyGroupByOption.DueDate: {
          if (item.dueDate == null) {
            return "No Due Date";
          }

          const dueDate = DateTime.fromISO(item.dueDate);

          if (dueDate < DateTime.now()) {
            return "Past Due";
          } else if (dueDate < DateTime.now().plus({ week: 1 })) {
            return "Due Soon";
          } else {
            return "Due Later";
          }
        }
      }
    },
    [settings.groupByOption],
  );

  const groupSortByFunction = useCallback(
    (groupName: string) => {
      switch (settings.groupByOption) {
        case UserStrategyGroupByOption.Status:
          return _.sortBy(ACTION_ITEM_STATUSES, (x) => x.sortOrder)
            .map((x) => x.name)
            .indexOf(groupName);
        case UserStrategyGroupByOption.DueDate:
          return ["Past Due", "Due Soon", "Due Later", "No Due Date"].indexOf(groupName);
        case UserStrategyGroupByOption.None:
        case UserStrategyGroupByOption.Team:
        default:
          return groupName;
      }
    },
    [settings.groupByOption],
  );

  const groupedActionItems = useMemo(() => {
    if (isLoading || data === undefined) {
      return undefined;
    }

    const actionItems = _.groupBy(data.actionItems, groupByFunction);
    const sortedGroupNames = _.sortBy(_.keys(actionItems), groupSortByFunction);

    return sortedGroupNames.reduce((map, groupName) => {
      map.set(groupName, actionItems[groupName]);
      return map;
    }, new Map<string, ActionItemSummaryDto[]>());
  }, [data, groupByFunction, groupSortByFunction, isLoading]);

  function handleOwnershipChanged(actionItemGuid: string, actionItemDescription: string, removedOwners: UserDto[]) {
    if (removedOwners.some((x) => x.userId === sessionUserGuid)) {
      enqueueSnackbar(
        <SnackbarContent
          title="Success"
          content={
            <>
              <span>Your ownership was removed from </span>
              <Link href={Links.ActionItemDetail(actionItemGuid)}>{actionItemDescription}</Link>
            </>
          }
        />,
        {
          variant: "success",
        },
      );

      // Close drawer incase it's open.
      closeDrawer();
    }
  }

  return (
    <Stack spacing={0.5}>
      {isLoading || groupedActionItems === undefined ? (
        <AccordionSkeleton isTableView={props.isTableView} />
      ) : groupedActionItems.size === 0 ? (
        <Typography>Nothing here yet.</Typography>
      ) : (
        Array.from(groupedActionItems.keys()).map((groupName, index) => (
          <Accordion
            key={index}
            title={(expanded) => (
              <AccordionTitle
                title={groupName}
                subtitle={expanded ? undefined : `(${groupedActionItems.get(groupName)?.length})`}
              />
            )}
            accordionDetailsSx={{ px: props.isTableView ? 0 : 0.5, pb: props.isTableView ? 0 : 0.5 }}
            backgroundColor={theme.palette.grey[100]}
          >
            {props.isTableView ? (
              <ActionItemsTable
                tableId={`userStrategicOwnership-${props.userGuid}`}
                actionItems={groupedActionItems.get(groupName) ?? []}
                canEditPlan={sessionUserGuid === props.userGuid}
                onOwnershipChanged={handleOwnershipChanged}
                fetchActionItemSubitems={() => Promise.resolve()}
              />
            ) : (
              <ViewList
                dataSource={groupedActionItems.get(groupName)}
                renderItem={(item) => (
                  <ActionItemCard
                    actionItemGuid={item.guid}
                    canEdit={item.userCanEdit}
                    canEditPlan={item.userCanEdit && sessionUserGuid === props.userGuid}
                    showTeamChip
                    onOwnershipChanged={handleOwnershipChanged}
                  />
                )}
              />
            )}
          </Accordion>
        ))
      )}
    </Stack>
  );
};

export { UserStrategicOwnershipContent };
